import React, { useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  TextField,
  ClickAwayListener
} from "@mui/material";
import { DateRange } from "react-date-range";
import Select from "react-select";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import CollectionReceivedLineChart from "./UserReports/Charts/CollectionReceivedLineChart";
import NetAmountLineChart from "./UserReports/Charts/NetAmountLineChart";
import PendingOrdersBarChart from "./UserReports/Charts/PendingOrdersBarChart";
import SchemesOverviewBarChart from "./UserReports/Charts/SchemesOverviewBarChart";

import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AssessmentIcon from "@mui/icons-material/Assessment";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PendingActionsIcon from "@mui/icons-material/PendingActions";


/* ================= SIYARAM COLOR PALETTE ================= */
const COLORS = {
  primary: "#102A43",      // Navy
  accent: "#8B6F2F",       // Muted Gold
  danger: "#8C2F39",       // Maroon
  neutral: "#2F2F2F",
  bg: "#F5F7FA",
  card: "#FFFFFF",
  border: "#E1E6ED",
  textSecondary: "#6B7785"
};

/* ================= KPI TILE (WITH ICON) ================= */
const KpiTile = ({ title, value, subtitle, color, icon }) => (
  <Paper
    sx={{
      p: 2.5,
      borderRadius: 3,
      height: 110,
      background: COLORS.card,
      borderTop: `6px solid ${color}`,
      boxShadow: "0 6px 18px rgba(16,42,67,0.08)",
      transition: "all 0.25s ease",
      cursor: "pointer",

      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 12px 28px rgba(16,42,67,0.18)"
      }
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
      <Typography fontSize={13} color={COLORS.textSecondary} fontWeight={800}>
        {title}
      </Typography>
      <Box sx={{ color, display: "flex", alignItems: "center" }}>
        {icon}
      </Box>
    </Box>

    <Typography fontSize={26} fontWeight={800} color={COLORS.primary}>
      {value}
    </Typography>

    <Typography fontSize={12} color={COLORS.textSecondary}>
      {subtitle}
    </Typography>
  </Paper>
);


/* ================= CHART CARD ================= */
const ChartCard = ({ title, children }) => (
  <Paper
    sx={{
      p: 2,
      borderRadius: 3,
      height: "100%",
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      cursor: "pointer",
      transition: "all 0.25s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 12px 28px rgba(16,42,67,0.18)"
      }

    }}
  >
    <Typography fontWeight={700} mb={1} color={COLORS.primary}>
      {title}
    </Typography>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

/* ================= SELECT STYLE ================= */
const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 42,
    borderRadius: 6,
    borderColor: COLORS.border,
    boxShadow: "none",
    "&:hover": { borderColor: COLORS.accent }
  })
};

/* ================= DATA ================= */
const collectionReceivedDummyData = [
  { month: "Apr-2025", collection_received: 14530, outstanding: 9800 },
  { month: "May-2025", collection_received: 19008, outstanding: 11200 },
  { month: "Jun-2025", collection_received: 15541, outstanding: 12000 },
  { month: "Sep-2025", collection_received: 24239, outstanding: 12300 },
  { month: "Nov-2025", collection_received: 15000, outstanding: 15000 },
  { month: "Jan-2026", collection_received: 12547, outstanding: 14800 }
];

const agingdata = [
  { week_start: "2024-03-11", net_outstanding: 85, days_30: 25, days_90: 10, days_180: 2 },
  { week_start: "2024-03-18", net_outstanding: 95, days_30: 27, days_90: 9, days_180: 0 },
  { week_start: "2024-03-25", net_outstanding: 105, days_30: 28, days_90: 7, days_180: 15 },
  { week_start: "2024-04-01", net_outstanding: 125, days_30: 35, days_90: 14, days_180: 2 },
  { week_start: "2024-04-15", net_outstanding: 135, days_30: 38, days_90: 14, days_180: 2 }
];

/* ================= MAIN COMPONENT ================= */
const ReportsDashboard = () => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [range, setRange] = useState([
    { startDate: null, endDate: null, key: "selection" }
  ]);

  return (
    <Box sx={{ p: 3, background: COLORS.bg, minHeight: "100vh" }}>
      <Typography variant="h5" fontWeight={800} mb={3} color={COLORS.primary}>
        Siyaram Reports Dashboard
      </Typography>

      {/* FILTER BAR */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3} sx={{ position: "relative" }}>
            <TextField
              fullWidth
              placeholder="DATE RANGE"
              onClick={() => setShowCalendar(!showCalendar)}
              value={
                range[0].startDate && range[0].endDate
                  ? `${range[0].startDate.toLocaleDateString("en-GB")} - ${range[0].endDate.toLocaleDateString("en-GB")}`
                  : ""
              }
              InputProps={{ readOnly: true }}
            />
            {showCalendar && (
              <ClickAwayListener onClickAway={() => setShowCalendar(false)}>
                <Box sx={{ position: "absolute", zIndex: 10 }}>
                  <DateRange
                    ranges={range}
                    onChange={(item) => {
                      setRange([item.selection]);
                      setShowCalendar(false);
                    }}
                  />
                </Box>
              </ClickAwayListener>
            )}
          </Grid>

          <Grid item md={4}>
            <Select placeholder="ZONE" isMulti styles={selectStyles} />
          </Grid>

          <Grid item md={5}>
            <Select placeholder="CUSTOMER" isMulti styles={selectStyles} />
          </Grid>
        </Grid>
      </Paper>

      {/* KPI ROW */}
      <Grid container spacing={2} mb={3}>
        <Grid item md={3}>
          <KpiTile
            title="Collected"
            value="₹ 3.6 Cr"
            subtitle="Last 6 months"
            color={COLORS.primary}
            icon={<AccountBalanceWalletIcon fontSize="small" />}
          />
        </Grid>

        <Grid item md={3}>
          <KpiTile
            title="Net Outstanding"
            value="₹ 1.1 Cr"
            subtitle="Current"
            color={COLORS.danger}
            icon={<AssessmentIcon fontSize="small" />}
          />
        </Grid>

        <Grid item md={3}>
          <KpiTile
            title="Active Schemes"
            value="3 | ₹1.5 Cr"
            subtitle="Running"
            color={COLORS.accent}
            icon={<Inventory2Icon fontSize="small" />}
          />
        </Grid>

        <Grid item md={3}>
          <KpiTile
            title="Pending Orders"
            value="6,580"
            subtitle="Open orders"
            color={COLORS.neutral}
            icon={<PendingActionsIcon fontSize="small" />}
          />
        </Grid>
      </Grid>


      {/* CHART GRID */}
      <Grid container spacing={2}>
        <Grid item md={7}>
          <ChartCard title="Collection Received">
            <CollectionReceivedLineChart data={collectionReceivedDummyData} />
          </ChartCard>
        </Grid>

        <Grid item md={5}>
          <ChartCard title="Outstanding Aging">
            <NetAmountLineChart data={agingdata} />
          </ChartCard>
        </Grid>

        <Grid item md={4}>
          <ChartCard title="Schemes Overview">
            <SchemesOverviewBarChart />
          </ChartCard>
        </Grid>

        <Grid item md={8}>
          <ChartCard title="Pending Orders">
            <PendingOrdersBarChart />
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportsDashboard;
